import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";

export default function Laboratory() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try { const { data } = await api.get("/projects"); setProjects(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/projects", { name, description: desc });
      nav(`/sprint/${data.project_id}`);
    } finally { setBusy(false); }
  };

  const remove = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm("Delete this sprint?")) return;
    await api.delete(`/projects/${id}`);
    load();
  };

  const fork = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const { data } = await api.post(`/projects/${id}/fork`);
      nav(`/sprint/${data.project_id}`);
    } catch (ex) {
      alert("Fork failed: " + (ex?.response?.data?.detail || "unknown"));
    }
  };

  return (
    <div data-testid="laboratory-page" style={{ padding: "60px 6vw 100px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 40, flexWrap: "wrap", gap: 20 }}>
        <div>
          <div className="overline" style={{ marginBottom: 14 }}>§ The Laboratory</div>
          <h1 className="font-serif" style={{ fontSize: "clamp(40px, 5vw, 64px)", lineHeight: 1, marginBottom: 8 }}>
            Your <span className="font-display" style={{ color: "var(--brass)" }}>sprints</span>, inscribed.
          </h1>
          <p className="font-serif" style={{ color: "var(--parchment-dim)", fontStyle: "italic", fontSize: 17 }}>
            Every project begins as a conversation. Begin one.
          </p>
        </div>
        <button data-testid="new-sprint-btn" className="btn-brass" onClick={() => setShowNew(true)}>
          + New Sprint
        </button>
      </div>

      <div className="rule-parchment" style={{ marginBottom: 40 }} />

      {showNew && (
        <div
          data-testid="new-sprint-modal"
          style={{
            position: "fixed", inset: 0, background: "rgba(9,10,15,0.85)",
            zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center",
            backdropFilter: "blur(6px)", padding: 20,
          }}
          onClick={() => !busy && setShowNew(false)}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={create}
            className="glass"
            style={{ width: "min(560px, 92vw)", padding: 44, display: "flex", flexDirection: "column", gap: 20 }}
          >
            <div className="overline">New Sprint</div>
            <h3 className="font-serif" style={{ fontSize: 32, marginBottom: 8 }}>
              What shall we architect?
            </h3>
            <div>
              <label className="label">Name</label>
              <input
                data-testid="new-sprint-name"
                autoFocus required
                className="input-field"
                value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Athena — an AI notebook for philosophers"
              />
            </div>
            <div>
              <label className="label">Description (optional)</label>
              <textarea
                data-testid="new-sprint-desc"
                className="input-field" rows={3}
                value={desc} onChange={(e) => setDesc(e.target.value)}
                placeholder="One or two sentences..."
              />
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 8 }}>
              <button type="button" className="btn-ghost" onClick={() => setShowNew(false)} disabled={busy}>Cancel</button>
              <button type="submit" data-testid="new-sprint-create-btn" className="btn-brass" disabled={busy}>
                {busy ? "Opening..." : "Begin Sprint →"}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="font-mono" style={{ color: "var(--parchment-dim)", fontSize: 12, letterSpacing: "0.2em" }}>
          Loading Codex...
        </div>
      ) : projects.length === 0 ? (
        <div
          data-testid="empty-laboratory"
          style={{
            border: "1px dashed rgba(197,160,89,0.35)", padding: 80, textAlign: "center",
          }}
        >
          <div className="font-display" style={{ color: "var(--brass)", fontSize: 54, fontStyle: "italic" }}>Ø</div>
          <h3 className="font-serif" style={{ fontSize: 28, marginTop: 8, marginBottom: 10 }}>The Codex is blank.</h3>
          <p className="font-serif" style={{ color: "var(--parchment-dim)", fontStyle: "italic", marginBottom: 24 }}>
            Open your first Sprint. The Mentor awaits.
          </p>
          <button className="btn-brass" onClick={() => setShowNew(true)} data-testid="empty-new-sprint-btn">
            + Open Sprint
          </button>
        </div>
      ) : (
        <div
          style={{
            display: "grid", gap: 2,
            gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
            background: "rgba(197,160,89,0.18)",
            border: "1px solid rgba(197,160,89,0.18)",
          }}
        >
          {projects.map((p, i) => (
            <Link
              key={p.project_id}
              to={`/sprint/${p.project_id}`}
              data-testid={`sprint-card-${p.project_id}`}
              className="reveal"
              style={{
                position: "relative",
                textDecoration: "none",
                color: "inherit",
                padding: 28,
                background: "var(--ink-surface)",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                minHeight: 220,
                animationDelay: `${0.05 * i}s`,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div className="overline" style={{ color: "var(--brass)" }}>
                  Sprint {String(i + 1).padStart(2, "0")}
                  {p.forked_from && <span style={{ marginLeft: 8, color: "var(--muted)" }}>· forked</span>}
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    data-testid={`fork-sprint-${p.project_id}`}
                    className="font-mono"
                    title="Fork this sprint into a new one — keeps Discovery/Audit/Generation but resets Chaos + Deploy"
                    style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 11, cursor: "pointer", letterSpacing: "0.2em" }}
                    onClick={(e) => fork(p.project_id, e)}
                  >
                    FORK
                  </button>
                  <button
                    data-testid={`delete-sprint-${p.project_id}`}
                    className="font-mono"
                    style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 11, cursor: "pointer", letterSpacing: "0.2em" }}
                    onClick={(e) => remove(p.project_id, e)}
                  >
                    DEL
                  </button>
                </div>
              </div>
              <h3 className="font-serif" style={{ fontSize: 26, lineHeight: 1.15, margin: 0 }}>{p.name}</h3>
              <p className="font-mono" style={{ fontSize: 12, color: "var(--parchment-dim)", lineHeight: 1.5, flex: 1, margin: 0 }}>
                {(p.description || "— no description —").slice(0, 140)}
              </p>
              <div style={{ display: "flex", gap: 12, fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--parchment-dim)" }}>
                <span>Phase {p.phase}/5</span>
                <span>·</span>
                <span style={{ color: p.chaos?.passed ? "var(--success)" : "var(--warning)" }}>
                  {p.chaos?.passed ? "Chaos: PASS" : "Chaos: —"}
                </span>
                {p.deployed && <><span>·</span><span style={{ color: "var(--brass)" }}>Deployed</span></>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
